import React from 'react';
import { Link } from 'react-router-dom';
import SeoLandingLayout from '../../components/SeoLandingLayout/SeoLandingLayout';

const CANONICAL = 'https://e-asy.es/servicios/ingenieria-a-cuota';

const SERVICE_LD = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Ingeniería a cuota',
  alternateName: [
    'Outsourcing tecnológico',
    'Outsourcing informático',
    'Ingeniero externo para pymes',
    'Informático externo',
    'Desarrollo a medida por cuota mensual',
  ],
  description:
    'Outsourcing tecnológico para pymes y ONG: desarrollo a medida dentro de la gestión y acompañamiento de la digitalización, por una cuota mensual. Un ingeniero especialista en la empresa, con una consultora detrás.',
  url: CANONICAL,
  serviceType: 'Outsourcing tecnológico e ingeniería a cuota',
  provider: {
    '@type': 'Organization',
    name: 'E-ASY',
    url: 'https://e-asy.es',
  },
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'Canarias' },
    { '@type': 'Country', name: 'España' },
  ],
};

const BREADCRUMB_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://e-asy.es/' },
    { '@type': 'ListItem', position: 2, name: 'Servicios', item: 'https://e-asy.es/servicios' },
    { '@type': 'ListItem', position: 3, name: 'Ingeniería a cuota', item: CANONICAL },
  ],
};

const FAQ = [
  {
    question: '¿Qué es el outsourcing tecnológico?',
    answer:
      'Externalizar la capacidad técnica: en vez de contratar un ingeniero en plantilla, una empresa encarga el desarrollo y el criterio tecnológico a un proveedor. En E-ASY eso es la ingeniería a cuota. No es enviar el trabajo a un equipo anónimo ni alquilar una persona suelta.',
  },
  {
    question: '¿Es lo mismo que contratar un informático?',
    answer:
      'El efecto en el día a día se parece: hay alguien que entiende la operación, desarrolla lo que el estándar no cubre y responde cuando algo se atasca. La diferencia es el contrato. No hay nómina, selección ni dependencia de una sola persona. Si cambia quién lo ejecuta, el trabajo sigue, porque detrás está la consultora.',
  },
  {
    question: '¿La cuota mensual equivale a una nómina?',
    answer:
      'Sirve como orden de magnitud: una cuota continua, del orden de lo que costaría un especialista en plantilla, a cambio de capacidad de desarrollo y acompañamiento. No publicamos un precio cerrado porque el alcance se acuerda. No sustituye un contrato laboral ni incluye Seguridad Social.',
  },
  {
    question: '¿Qué diferencia hay con un proyecto de software a medida?',
    answer:
      'El software a medida es un alcance cerrado: se define, se construye y se entrega. La ingeniería a cuota es capacidad mes a mes dentro de la gestión, con prioridad que cambia. Si ya sabéis exactamente qué hay que construir y cuándo acaba, el camino es la ficha de software de gestión a medida.',
  },
  {
    question: '¿Incluye Microsoft 365, Azure o el mantenimiento de una app de IA?',
    answer:
      'No como producto de esta ficha. Cuentas, identidad y cloud están en la consultoría Microsoft / Azure. Dejar una aplicación de IA en producción, con hosting y soporte, es el retainer de IA en producción. Aquí el centro es desarrollar y acompañar la digitalización de la operación.',
  },
  {
    question: '¿Sirve para una pyme sin departamento de informática?',
    answer:
      'Sí. Está pensado para pymes y ONG que no van a montar un equipo de desarrollo y aun así necesitan que alguien meta mano en la gestión: procesos, herramientas y el software que el paquete estándar no resuelve.',
  },
];

const IngenieriaACuotaPage = ({ darkMode, toggleDarkMode }) => (
  <SeoLandingLayout
    darkMode={darkMode}
    toggleDarkMode={toggleDarkMode}
    title="Ingeniero externo para pymes | Outsourcing tecnológico | E-ASY"
    description="Outsourcing tecnológico para pymes: un ingeniero especialista en tu empresa, con una consultora detrás. Desarrollo a medida y digitalización por una cuota mensual, sin contratar en plantilla."
    keywords="outsourcing tecnológico, outsourcing informático, ingeniero externo empresa, informático externo pyme, externalizar desarrollo software, desarrollo a medida cuota mensual, equipo de desarrollo externo"
    canonical={CANONICAL}
    h1="Un ingeniero en tu empresa, por una cuota mensual"
    lede="Desarrollo a medida dentro de tu gestión y acompañamiento en la digitalización, como si tuvieras un especialista en plantilla. No contratas a una persona: contratas a una consultora completa."
    faqItems={FAQ}
    serviceJsonLd={SERVICE_LD}
    serviceJsonLdId="jsonld-service-ingenieria-cuota"
    breadcrumbJsonLd={BREADCRUMB_LD}
    cobertura
    ctaLabel="Hablar de la cuota"
    footerCtaLabel="Cuéntanos cómo trabajáis hoy"
  >
    <h2>Outsourcing informático, cuando no quieres montar un departamento</h2>
    <p>
      Muchas pymes llegan con la misma frase: necesitamos un informático, o un
      ingeniero que entienda cómo trabajamos. Contratarlo en plantilla implica
      selección, vacaciones, bajas y que el conocimiento se vaya si esa persona
      se va. Externalizar el desarrollo evita eso, siempre que detrás no haya
      un freelance solo.
    </p>
    <p>
      La <strong>ingeniería a cuota</strong> es esa capacidad continua. Cada mes
      hay desarrollo sobre la operación real y alguien con criterio para decidir
      qué se digitaliza ahora y qué puede esperar. La cuota es del orden de lo
      que costaría un especialista. El alcance se acuerda; no hay una tarifa
      publicada porque no todas las operaciones pesan igual.
    </p>

    <h2>Qué entra en la cuota</h2>
    <ul>
      <li>
        <strong>Desarrollo a medida</strong> dentro de la gestión: lo que el
        ERP, la hoja o el programa de siempre no cubren, construido sobre cómo
        trabajáis de verdad.
      </li>
      <li>
        Prioridad del mes. El encargo cambia; no hace falta abrir un proyecto
        nuevo cada vez que aparece un cuello de botella.
      </li>
      <li>
        Acompañamiento de la digitalización: qué herramienta tiene sentido,
        qué se integra y qué no merece la pena construir.
      </li>
      <li>
        El resto de la consultora cuando el caso se sale del desarrollo.{' '}
        <Link to="/vulpin" className="vulpin-inline-link">Vulpin</Link>,{' '}
        <Link to="/servicios/ciberseguridad-cumplimiento" className="vulpin-inline-link">
          ciberseguridad
        </Link>
        ,{' '}
        <Link to="/servicios/ia-en-produccion" className="vulpin-inline-link">
          IA en producción
        </Link>{' '}
        o{' '}
        <Link to="/servicios/consultoria-microsoft-azure" className="vulpin-inline-link">
          Microsoft y Azure
        </Link>{' '}
        entran sin cambiar de proveedor.
      </li>
    </ul>

    <h2>Qué se gana respecto a un contratado</h2>
    <ul>
      <li>
        Continuidad. El trabajo no se para porque alguien esté de baja o cambie
        de empresa.
      </li>
      <li>
        Criterio de más de una persona. Un especialista en plantilla sabe lo
        que sabe; aquí el caso se puede apoyar en quien lleva ERP, datos o
        seguridad.
      </li>
      <li>
        Sin proceso de selección. La cuota es el acuerdo continuo: no hay que
        abrir una vacante cada vez que aparece trabajo de desarrollo.
      </li>
      <li>
        El software queda en vuestra operación, no en un entregable que nadie
        mantiene al mes siguiente.
      </li>
    </ul>

    <h2>Cuándo es esta cuota y cuándo es otra cosa</h2>
    <p>
      Si ya está definido qué hay que construir y el trabajo acaba al
      entregarlo, eso es un proyecto de{' '}
      <Link to="/servicios/software-gestion-medida">software de gestión a medida</Link>.
      Si lo que falta es correo, identidad, Azure o el ERP de Microsoft, la
      ficha es la{' '}
      <Link to="/servicios/consultoria-microsoft-azure">
        consultoría Microsoft / Azure
      </Link>
      . Si hay que dejar una aplicación de IA viva, con hosting y alguien que
      responda el viernes, es{' '}
      <Link to="/servicios/ia-en-produccion">IA en producción</Link>.
    </p>
    <p>
      Esta página es la capacidad de en medio: desarrollo y digitalización mes
      a mes, para una pyme o una ONG que no va a tener departamento de
      informática y aun así no puede seguir con parches.
    </p>
  </SeoLandingLayout>
);

export default IngenieriaACuotaPage;
